import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api.js';
import Spinner from '../components/Spinner.jsx';
import Badge from '../components/Badge.jsx';
import ProgressBar from '../components/ProgressBar.jsx';

const TABS = [
  { key: 'theorie',           label: '📚 Theorie' },
  { key: 'praxis_trocken',    label: '🏋 Trocken' },
  { key: 'ausbildungstauchgang', label: '🤿 Tauchgänge' },
];

export default function StudentDetail() {
  const { id } = useParams();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('theorie');
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving]   = useState(null);
  const [notiz, setNotiz]     = useState('');

  const load = () => {
    setLoading(true);
    api.getProgress(id)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  if (loading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  if (!data)   return <p className="text-red-600">Fehler beim Laden.</p>;

  const items   = data.items ?? [];
  const student = data.student;
  const filtered = items.filter((i) => i.category === tab);
  const done     = items.filter((i) => i.status !== 'offen').length;

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Link to="/ausbilder" className="hover:text-blue-600">Schüler</Link>
        <span>/</span>
        <span className="font-medium text-gray-900">{student.kuerzel}</span>
        <span className="badge bg-blue-50 text-blue-700 ml-1">Stufe {student.stufe_ziel}</span>
      </div>

      <ProgressBar done={done} total={items.length} />

      <div className="flex gap-1 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-blue-700 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {t.label}
          </button>
        ))}
        <Link to={`/ausbilder/schueler/${id}/tauchgaenge`} className="btn-ghost text-xs ml-auto">
          Tauchgang-Log →
        </Link>
      </div>

      {/* Item-Liste */}
      <div className="space-y-2">
        {filtered.map((item) => (
          <div key={item.id} className="card border-l-4"
            style={{ borderLeftColor: item.status === 'offen' ? '#e5e7eb' : item.status === 'geprueft' ? '#16a34a' : item.status === 'geubt' ? '#d97706' : '#2563eb' }}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-gray-900">{item.title}</span>
                  <Badge type={item.requirement} />
                  <Badge type={item.status} />
                </div>
                <p className="text-xs text-gray-400 mt-0.5">{item.topic}{item.fwdv8_ref ? ` · ${item.fwdv8_ref}` : ''}</p>
                {expanded === item.id && (
                  <p className="text-xs text-gray-600 mt-2 bg-gray-50 rounded p-2">{item.description}</p>
                )}
                {item.datum && (
                  <p className="text-xs text-gray-400 mt-1">
                    {item.datum} · {item.ausbilder?.split('@')[0]}{item.notiz && ` · „${item.notiz}"`}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button
                  onClick={() => setExpanded(expanded === item.id ? null : item.id)}
                  className="btn-ghost text-xs px-2 py-1"
                  aria-expanded={expanded === item.id}
                  aria-label={expanded === item.id ? `Details zu „${item.title}" einklappen` : `Details zu „${item.title}" ausklappen`}
                >
                  <span aria-hidden="true">{expanded === item.id ? '▲' : '▼'}</span>
                </button>
                <button
                  onClick={() => { setExpanded(item.id); }}
                  disabled={saving === item.id}
                  className="btn-secondary text-xs"
                  aria-label={item.status === 'offen'
                    ? `Lernziel „${item.title}" abhaken`
                    : `Status von „${item.title}" ändern`}
                >
                  <span aria-hidden="true">{item.status === 'offen' ? '✓' : '↻'}</span>
                </button>
              </div>
            </div>

            {/* Inline-Form */}
            {expanded === item.id && (
              <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                <input
                  className="input text-sm"
                  placeholder="Notiz (optional)"
                  value={notiz}
                  onChange={(e) => setNotiz(e.target.value)}
                />
                <div className="flex gap-2 flex-wrap">
                  {['vermittelt','geubt','geprueft','offen'].map((s) => (
                    <button
                      key={s}
                      onClick={async () => {
                        setSaving(item.id);
                        try {
                          await api.setProgress(id, { item_id: item.id, status: s, notiz: notiz || undefined });
                          load();
                          setExpanded(null);
                          setNotiz('');
                        } finally { setSaving(null); }
                      }}
                      disabled={s === item.status || saving === item.id}
                      className={`btn text-xs ${s === item.status ? 'opacity-40 cursor-default bg-gray-100 text-gray-500' : 'btn-secondary'}`}
                    >
                      <Badge type={s} />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
